import colors from '../../theme/colors';
import PropTypes from 'prop-types';
import { AlertCircle, Wifi, FileX, HelpCircle } from 'lucide-react';
import ErrorDisplay from '../error/ErrorDisplay';

/**
 * TenderErrorHandler - Specialized error handling for tender analysis
 *
 * This component provides context-aware error handling for tender analysis,
 * using the existing ErrorDisplay component with tender-specific configurations.
 */
const TenderErrorHandler = ({
  status,
  error,
  userMessage,
  suggestions,
  canRetry,
  errorType,
}) => {
  const getTenderErrorConfig = () => {
    // If we have backend error details from tenderData, use them
    if (userMessage || suggestions || errorType) {
      return {
        code: errorType
          ? `TENDER_${errorType.toUpperCase()}`
          : 'TENDER_ANALYSIS_ERROR',
        type: errorType || 'system_error',
        user_message: userMessage || 'Tender analysis failed',
        message:
          userMessage ||
          error?.message ||
          'An error occurred during tender analysis.',
        suggestions: suggestions || [
          'Try refreshing the page',
          'Contact support if the issue persists',
        ],
        severity: canRetry ? 'medium' : 'high',
        icon: <AlertCircle className="error-icon" />,
      };
    }

    // Fallback error configs for status-based handling
    switch (status) {
      case 'FAILURE':
        return {
          code: 'TENDER_ANALYSIS_FAILURE',
          type: 'system_error',
          user_message: 'Analysis failed',
          message:
            userMessage ||
            error?.message ||
            'The tender analysis process encountered an error.',
          suggestions: [
            'Ensure all uploaded documents are valid and readable',
            'Contact support if the issue persists',
          ],
          severity: 'high',
          icon: <AlertCircle className="error-icon" />,
        };

      case 'UNPROCESSABLE':
        return {
          code: 'TENDER_ANALYSIS_PARTIAL',
          type: 'validation_error',
          user_message: 'Analysis incomplete',
          message:
            'The analysis could not be completed due to issues processing some documents.',
          suggestions: [
            'Check if any uploaded documents are corrupted or unreadable',
            'Ensure all documents are in supported formats (PDF, Excel)',
            'Try re-uploading the documents and running the analysis again',
          ],
          severity: 'medium',
          icon: <FileX className="warning-icon" />,
        };

      case 'server_error':
        return {
          code: 'TENDER_SERVER_ERROR',
          type: 'system_error',
          user_message: 'Analysis server error',
          message:
            'A server error occurred while processing the tender analysis.',
          suggestions: [
            'This is typically a temporary issue',
            'Try refreshing the page or checking back later',
            'Contact support if the problem persists',
          ],
          severity: 'high',
          icon: <AlertCircle className="error-icon" />,
        };

      case 'network_error':
        return {
          code: 'TENDER_NETWORK_ERROR',
          type: 'network_error',
          user_message: 'Connection error',
          message: 'Unable to fetch tender analysis due to a network error.',
          suggestions: [
            'Check your internet connection',
            'Try refreshing the page',
            'Contact support if connectivity issues persist',
          ],
          severity: 'medium',
          icon: <Wifi className="error-icon" />,
        };

      case 'bad_request':
        return {
          code: 'TENDER_DATA_ERROR',
          type: 'validation_error',
          user_message: 'Analysis data error',
          message: 'There was an issue with the tender analysis data format.',
          suggestions: [
            'This may indicate corrupted analysis data',
            'Try re-uploading the package if possible',
            'Contact support for assistance with data recovery',
          ],
          severity: 'high',
          icon: <AlertCircle className="error-icon" />,
        };

      default:
        return {
          code: 'TENDER_UNKNOWN_ERROR',
          type: 'system_error',
          user_message: 'Unknown error',
          message:
            userMessage ||
            error?.message ||
            'An unexpected error occurred during tender analysis.',
          suggestions: [
            'Try refreshing the page',
            'Contact support if the issue persists',
          ],
          severity: 'medium',
          icon: <HelpCircle className="error-icon" />,
        };
    }
  };

  // Don't show anything if there's no error
  const skipStatuses = ['success', 'pending', 'started', 'loading', 'idle'];
  if (!status || skipStatuses.includes(status?.toLowerCase())) {
    return null;
  }

  const config = getTenderErrorConfig();

  // Create error object compatible with ErrorDisplay
  const errorForDisplay = {
    code: config.code,
    type: config.type,
    message: config.message,
    user_message: config.user_message,
    suggestions: config.suggestions,
    severity: config.severity,
    reference_id: error?.reference_id || `tender_${Date.now().toString(36)}`,
    details: {
      status,
      errorType,
      canRetry,
      originalError: error,
    },
  };

  return (
    <div className="tender-error-handler">
      <ErrorDisplay
        error={errorForDisplay}
        showSuggestions={true}
        showContactSupport={config.severity === 'high'}
        showErrorCode={true}
        showErrorDetails={false}
        variant="standard"
        sx={{
          mt: 0,
          mb: 2,
        }}
      />

      {/* Additional context */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: '12px',
          padding: '12px',
          backgroundColor: colors.background.secondary,
          borderRadius: '6px',
          border: '1px solid #e9ecef',
        }}
      >
        <div
          style={{
            fontSize: '13px',
            color: colors.grey.t500,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <strong style={{ color: colors.red.main }}>Note:</strong>
          The tender analysis is required before other analyses can be
          performed. Please resolve this issue to proceed.
        </div>
      </div>
    </div>
  );
};

TenderErrorHandler.propTypes = {
  /**
   * Error status from tender analysis
   */
  status: PropTypes.oneOf([
    'FAILURE',
    'UNPROCESSABLE',
    'server_error',
    'network_error',
    'bad_request',
    'unknown_error',
    'SUCCESS',
    'success',
    'PENDING',
    'STARTED',
    'loading',
    'IDLE',
  ]).isRequired,

  /**
   * Error object with details
   */
  error: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string,
    user_message: PropTypes.string,
    suggestions: PropTypes.arrayOf(PropTypes.string),
    canRetry: PropTypes.bool,
    reference_id: PropTypes.string,
  }),

  /**
   * User-friendly error message from backend
   */
  userMessage: PropTypes.string,

  /**
   * Error suggestions from backend
   */
  suggestions: PropTypes.arrayOf(PropTypes.string),

  /**
   * Whether the analysis can be retried
   */
  canRetry: PropTypes.bool,

  /**
   * Error type classification from backend
   */
  errorType: PropTypes.string,
};

export default TenderErrorHandler;
